import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/lib/i18n";

export function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-white/15" data-testid="lang-toggle">
      {["en", "pt"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 font-mono text-[10px] tracking-[0.15em] uppercase transition-colors duration-300 ${
            lang === l ? "bg-gold text-ink" : "text-white/50 hover:text-ivory"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Nav() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const LINKS = [
    { label: t("nav.about"), href: "#about" },
    { label: t("nav.bsgotech"), href: "#bsgotech" },
    { label: t("nav.projects"), href: "#projects" },
    { label: t("nav.focus"), href: "#focus" },
    { label: t("nav.journey"), href: "#journey" },
    { label: t("nav.resources"), href: "#resources" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="text-left">
          <span className="block font-serif text-xl tracking-wide text-ivory">Cleiton Barreto dos Santos</span>
          <span className="block font-mono text-[10px] tracking-[0.35em] uppercase text-gold mt-0.5">BSGOTECH · Founder Platform</span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/60 hover:text-ivory transition-colors duration-300 gold-underline pb-1"
            >
              {l.label}
            </button>
          ))}
          <LangToggle />
          <button
            data-testid="nav-connect-btn"
            onClick={() => go("#connect")}
            className="px-6 py-2.5 bg-gold text-ink font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-light transition-colors duration-300"
          >
            {t("nav.connect")}
          </button>
        </nav>

        <div className="lg:hidden flex items-center gap-4">
          <LangToggle />
          <button data-testid="nav-mobile-toggle" className="text-ivory" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-ink/95 backdrop-blur-xl border-b border-white/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button key={l.href} data-testid={`nav-mobile-${l.href.slice(1)}`} onClick={() => go(l.href)} className="text-left font-mono text-xs tracking-[0.2em] uppercase text-white/70">
                  {l.label}
                </button>
              ))}
              <button data-testid="nav-mobile-connect" onClick={() => go("#connect")} className="mt-2 px-6 py-3 bg-gold text-ink font-mono text-[11px] tracking-[0.2em] uppercase w-max">
                {t("nav.connect")}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
